'use client'

import type { Common } from '@cruz-com/lookup'
import { autoUpdate, flip, offset, shift, size, useFloating } from '@floating-ui/react'
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react'
import _ from 'lodash'
import type React from 'react'
import { HiChevronUpDown } from 'react-icons/hi2'
import { twMerge } from 'tailwind-merge'

type ListBoxProps = {
  label?: string | React.ReactNode
  id?: string
  name?: string
  inline?: boolean
  compact?: boolean
  option: Common.Option[]
  value?: string | number
  onChange: (val: string | number) => void
  optional?: boolean
  disabled?: boolean
} & Omit<React.ComponentPropsWithoutRef<'select'>, 'onChange'>

export function ListBox(props: ListBoxProps) {
  const { label, id, name, inline, compact, option, value, onChange, className, optional, disabled } = props
  const errMsg = _.get(props, 'data-error')
  const fieldId = id || name || `listbox-${label?.toString().replace(/\s+/g, '-').toLowerCase()}`

  const {
    refs,
    floatingStyles,
    placement: _placement,
  } = useFloating({
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(2),
      flip({
        fallbackPlacements: ['top-start', 'bottom-start'],
        fallbackAxisSideDirection: 'start',
        padding: 8,
      }),
      shift({ padding: 8 }),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            minWidth: `${rects.reference.width}px`,
            maxWidth: '400px',
          })
        },
        padding: 8,
      }),
    ],
  })

  function handleChange(val: number | string) {
    if (val?.toString() === value?.toString()) return
    onChange(val)
  }
  return (
    <Listbox name={name} value={value?.toString()} onChange={(val) => handleChange(val)} disabled={disabled || false}>
      <div className={twMerge(className, inline ? '' : 'w-full')}>
        {label && (
          <label id={`${fieldId}-label`} htmlFor={fieldId} className='form-label cursor-pointer'>
            {label}
            {optional && <span className='ml-1.5 font-light text-zinc-600 dark:text-zinc-400'>(optional)</span>}
          </label>
        )}
        <div className={twMerge('relative w-full', inline ? 'mt-0' : 'mt-1')}>
          <ListboxButton
            id={fieldId}
            ref={refs.setReference}
            aria-labelledby={label ? `${fieldId}-label` : undefined}
            aria-describedby={errMsg ? `${fieldId}-error` : undefined}
            aria-invalid={!!errMsg}
            className={twMerge('form-listbox', compact && 'md:h-9!', (compact || inline) && 'md:text-sm!', disabled && 'form-disabled')}
          >
            <span className='block truncate pr-8 pl-1'>
              {
                _.find(option as Common.Option[], (x) => {
                  return _.toString(x?.id) === value?.toString()
                })?.name
              }
            </span>
            <span className='form-input-icon'>
              <HiChevronUpDown aria-hidden='true' className='size-5 text-zinc-400' />
            </span>
          </ListboxButton>
          <ListboxOptions ref={refs.setFloating} style={floatingStyles} transition className='form-list-box-options'>
            {_.map(option, (o, idx) => (
              <ListboxOption key={idx} value={o?.id?.toString()} aria-label={o.name?.toString()} className='form-option'>
                {o.name}
              </ListboxOption>
            ))}
          </ListboxOptions>
        </div>
        {errMsg && (
          <div id={`${fieldId}-error`} aria-live='polite' className='form-error-message'>
            {errMsg}
          </div>
        )}
      </div>
    </Listbox>
  )
}
